import { ajaxAddToCart } from '../../woocommerce/cart-ajax.js';

export function initProductBundleShowcase() {
	const gsap = window.gsap;
	const ScrollTrigger = window.ScrollTrigger;

	document.querySelectorAll( '[data-bundle-showcase]' ).forEach( ( widget ) => {
		if ( widget.dataset.bundleShowcaseInit ) return;
		widget.dataset.bundleShowcaseInit = '1';

		initBundleAddButtons( widget );
		initBundleHotspots( widget );

		if (
			! gsap ||
			! ScrollTrigger ||
			window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches ||
			window.elementorFrontend?.isEditMode?.()
		) {
			return;
		}

		const stage = widget.querySelector( '.product-bundle-showcase__stage' );
		const cards = [ ...widget.querySelectorAll( '[data-bundle-card]' ) ];
		if ( ! stage || cards.length < 2 ) return;

		gsap.registerPlugin( ScrollTrigger );
		const media = gsap.matchMedia();

		media.add( '(min-width: 64em)', () => {
			stage.classList.add( 'is-enhanced' );
			gsap.set( cards, { autoAlpha: 0, pointerEvents: 'none' } );
			gsap.set( cards[ 0 ], { autoAlpha: 1, pointerEvents: 'auto' } );

			const progressDots = [ ...widget.querySelectorAll( '[data-bundle-progress]' ) ];
			const getPhaseDistance = () => Math.max( window.innerHeight * 0.65, 600 );
			const getScrollDistance = () => ( ( cards.length - 1 ) * getPhaseDistance() ) + 400;
			const getCardImage = ( card ) => card.querySelector( '.product-bundle-showcase__image' );
			const setActiveCard = ( index ) => {
				cards.forEach( ( card, cardIndex ) => {
					const isActive = cardIndex === index;
					card.classList.toggle( 'is-active', isActive );
					card.setAttribute( 'aria-hidden', String( ! isActive ) );
					card.inert = ! isActive;
				} );
				progressDots.forEach( ( dot, dotIndex ) => {
					dot.classList.toggle( 'is-active', dotIndex === index );
				} );
			};

			setActiveCard( 0 );

			let intro = gsap.fromTo(
				getCardAnimationItems( cards[ 0 ] ),
				{ autoAlpha: 0, y: 36 },
				{ autoAlpha: 1, y: 0, duration: 0.8, stagger: 0.1, ease: 'power3.out' }
			);
			let activeIndex = 0;
			let requestedIndex = 0;
			let transition;
			let isDestroyed = false;

			const runNextTransition = () => {
				if ( isDestroyed || transition || activeIndex === requestedIndex ) return;

				if ( intro ) {
					intro.progress( 1 ).kill();
					intro = null;
				}

				const direction = requestedIndex > activeIndex ? 1 : -1;
				const previousCard = cards[ activeIndex ];
				const nextIndex = activeIndex + direction;
				const nextCard = cards[ nextIndex ];
				const previousImage = getCardImage( previousCard );
				const nextImage = getCardImage( nextCard );
				const previousItems = getCardAnimationItems( previousCard );
				const nextItems = getCardAnimationItems( nextCard );
				const transitionTargets = [
					previousCard,
					nextCard,
					...previousItems,
					...nextItems,
					previousImage,
					nextImage,
				].filter( Boolean );

				gsap.set( nextCard, { autoAlpha: 0, scale: 1.04, pointerEvents: 'none' } );
				gsap.set( nextItems, { autoAlpha: 0, y: direction * 48 } );
				if ( nextImage ) gsap.set( nextImage, { scale: 1.12 } );
				gsap.set( transitionTargets, { willChange: 'transform, opacity' } );
				setActiveCard( nextIndex );

				transition = gsap.timeline( {
					onComplete: () => {
						gsap.set( transitionTargets, { clearProps: 'willChange' } );
						transition = null;
						activeIndex = nextIndex;
						runNextTransition();
					},
				} );
				transition
					.to(
						previousItems,
						{
							autoAlpha: 0,
							y: direction * -28,
							duration: 0.28,
							stagger: { each: 0.03, from: 'end' },
							ease: 'power2.in',
						}
					)
					.to(
						previousCard,
						{ autoAlpha: 0, scale: 0.97, pointerEvents: 'none', duration: 0.52, ease: 'power2.inOut' },
						'<'
					);
				if ( previousImage ) {
					transition.to( previousImage, { scale: 1.08, duration: 0.52, ease: 'power2.inOut' }, '<' );
				}
				transition.to(
					nextCard,
					{ autoAlpha: 1, scale: 1, pointerEvents: 'auto', duration: 0.52, ease: 'power3.out' },
					'<'
				);
				if ( nextImage ) {
					transition.to( nextImage, { scale: 1, duration: 0.62, ease: 'power3.out' }, '<' );
				}
				transition.to(
					nextItems,
					{ autoAlpha: 1, y: 0, duration: 0.4, stagger: 0.04, ease: 'power3.out' },
					'<0.12'
				);
			};

			const requestTransition = ( index ) => {
				requestedIndex = index;
				runNextTransition();
			};

			const getPhaseIndex = ( progress, direction = 0 ) => {
				const transitionLead = direction > 0 ? getPhaseDistance() * 0.1 : 0;
				return Math.min(
					cards.length - 1,
					Math.floor( ( ( progress * getScrollDistance() ) + transitionLead ) / getPhaseDistance() )
				);
			};

			const trigger = ScrollTrigger.create( {
				trigger: stage,
				start: 'top 112px',
				end: () => `+=${ getScrollDistance() }`,
				pin: true,
				invalidateOnRefresh: true,
				onUpdate: ( self ) => requestTransition( getPhaseIndex( self.progress, self.direction ) ),
				onRefresh: ( self ) => requestTransition( getPhaseIndex( self.progress ) ),
			} );

			return () => {
				isDestroyed = true;
				trigger.kill();
				intro?.kill();
				transition?.kill();
				stage.classList.remove( 'is-enhanced' );
				gsap.set(
					[
						...cards,
						...cards.flatMap( ( card ) => [ ...getCardAnimationItems( card ) ] ),
						...cards.map( getCardImage ).filter( Boolean ),
					],
					{ clearProps: 'all' }
				);
				cards.forEach( ( card, index ) => {
					card.classList.toggle( 'is-active', index === 0 );
					card.setAttribute( 'aria-hidden', 'false' );
					card.inert = false;
				} );
				progressDots.forEach( ( dot, index ) => {
					dot.classList.toggle( 'is-active', index === 0 );
				} );
			};
		} );
	} );
}

function getCardAnimationItems( card ) {
	return card.querySelectorAll(
		'.product-bundle-showcase__eyebrow, .product-bundle-showcase__content h3, .product-bundle-showcase__description, .product-bundle-showcase__products, .product-bundle-showcase__add, .product-bundle-showcase__content .buttons, .product-bundle-showcase__scroll-cue'
	);
}

function initBundleAddButtons( widget ) {
	widget.querySelectorAll( '[data-bundle-add]' ).forEach( ( button ) => {
		if ( button.dataset.bundleAddInit ) return;
		button.dataset.bundleAddInit = '1';

		button.addEventListener( 'click', async () => {
			const productIds = parseProductIds( button.dataset.productIds );
			if ( productIds.length === 0 || button.disabled ) return;

			await addBundleToCart( button, productIds );
		} );
	} );
}

function initBundleHotspots( widget ) {
	widget.querySelectorAll( '[data-bundle-card]' ).forEach( ( card ) => {
		const hotspots = [ ...card.querySelectorAll( '[data-bundle-hotspot]' ) ];
		if ( hotspots.length === 0 ) return;

		const setActiveProduct = ( productId ) => {
			hotspots.forEach( ( hotspot ) => {
				const isActive = hotspot.dataset.bundleHotspot === productId;
				hotspot.classList.toggle( 'is-active', isActive );
				hotspot.setAttribute( 'aria-pressed', String( isActive ) );
			} );
			card.querySelectorAll( '[data-bundle-product]' ).forEach( ( product ) => {
				product.classList.toggle( 'is-highlighted', product.dataset.bundleProduct === productId );
			} );
		};

		hotspots.forEach( ( hotspot ) => {
			hotspot.addEventListener( 'click', () => setActiveProduct( hotspot.dataset.bundleHotspot ) );
			hotspot.addEventListener( 'focus', () => setActiveProduct( hotspot.dataset.bundleHotspot ) );
		} );

		card.querySelectorAll( '[data-bundle-product]' ).forEach( ( product ) => {
			const activateProduct = () => setActiveProduct( product.dataset.bundleProduct );
			product.addEventListener( 'pointerenter', activateProduct );
			product.querySelector( 'a' )?.addEventListener( 'focus', activateProduct );
		} );
	} );
}

function parseProductIds( value ) {
	try {
		const ids = JSON.parse( value || '[]' );
		return Array.isArray( ids ) ? ids.filter( ( id ) => Number.isInteger( id ) && id > 0 ) : [];
	} catch {
		return [];
	}
}

async function addBundleToCart( button, productIds ) {
	const config = window.dbProductGrid || {};
	const messages = config.i18n || {};
	const label = button.querySelector( '.btn__label' );
	const originalLabel = label?.textContent || '';

	if ( ! config.addToCartUrl ) return;

	button.disabled = true;
	button.classList.add( 'is-loading' );
	if ( label ) label.textContent = messages.bundleAdding || originalLabel;

	let allSucceeded = true;

	for ( const productId of productIds ) {
		const result = await ajaxAddToCart( { productId } );
		if ( ! result.success ) {
			allSucceeded = false;
			break;
		}
	}

	button.classList.remove( 'is-loading' );

	if ( allSucceeded ) {
		button.classList.add( 'is-success' );
		if ( label ) label.textContent = messages.bundleAdded || originalLabel;
	} else {
		button.classList.add( 'is-error' );
		if ( label ) label.textContent = messages.bundleError || originalLabel;
	}

	window.setTimeout( () => {
		button.classList.remove( 'is-success', 'is-error' );
		button.disabled = false;
		if ( label ) label.textContent = originalLabel;
	}, 2500 );
}

